import styled from '@emotion/styled';
import { graphql, useStaticQuery } from 'gatsby';
import { type FC, useEffect, useState } from 'react';

import { Img } from '@/components/common/atoms/img';
import type { Props as WpImgProps } from '@/components/common/atoms/img/wp-img';
import { useLocationContext } from '@/components/common/providers/location-provider';
import { media, theme } from '@/styles';

type Props = {
  className?: string;
};

const staticQuery = graphql`
  query HomeEntry {
    wpPage(slug: { eq: "home" }) {
      acfHome {
        homeImages {
          nodes {
            ...WpImg
          }
        }
      }
    }
  }
`;

const HOME_IMAGE_SIZES = `(min-width: ${theme.breakpoints.ipadVertical}px) calc(100vw - ${
  theme.structure.main.margin.horizontal.pc
}px - ${theme.structure.page.headerWidth.pc * 5}px), calc(100vw - ${
  theme.structure.main.margin.horizontal.pc
}px)`;

let currentHomeImage: WpImgProps | null = null;
let nextHomeImage: WpImgProps | null = null;
let previousIsHome: boolean | null = null;

const getRandomInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1) + min);

const pickRandomImage = (images: readonly (WpImgProps | null)[]) => {
  const availableImages = images.filter(Boolean);

  if (!availableImages.length) {
    return null;
  }

  return availableImages[getRandomInt(0, availableImages.length - 1)] ?? null;
};

const preloadImage = (image: WpImgProps | null) => {
  if (!image || !image.sourceUrl || typeof window === 'undefined') return;

  const preloadTarget = new window.Image();

  const srcSet = [
    ...(image.mediaDetails?.sizes
      ?.filter(
        (size) =>
          size &&
          size.name !== 'icon' &&
          size.name !== 'line' &&
          size.name !== 'calendar'
      )
      .map((size) => `${size?.sourceUrl} ${size?.width}w`) ?? []),
    `${image.sourceUrl} ${image.width}w`,
  ].join(', ');

  preloadTarget.src = image.sourceUrl;
  preloadTarget.srcset = srcSet;
  preloadTarget.sizes = HOME_IMAGE_SIZES;
};

const useHomeImage = ({ wpPage }: Queries.HomeEntryQuery) => {
  const { isHome } = useLocationContext();
  const [image, setImage] = useState<WpImgProps | null>(currentHomeImage);
  const images = wpPage?.acfHome?.homeImages?.nodes ?? [];

  useEffect(() => {
    const wasHome = previousIsHome;

    if (!images.length) {
      currentHomeImage = null;
      nextHomeImage = null;
      previousIsHome = isHome;
      setImage(null);
      return;
    }

    if (!currentHomeImage) {
      currentHomeImage = pickRandomImage(images);
    }

    if (!nextHomeImage) {
      nextHomeImage = pickRandomImage(images);
      preloadImage(nextHomeImage);
    }

    if (isHome && wasHome === false) {
      currentHomeImage = nextHomeImage ?? currentHomeImage;
      nextHomeImage = pickRandomImage(images);
      preloadImage(nextHomeImage);
    }

    previousIsHome = isHome;
    setImage(currentHomeImage);
  }, [images, isHome]);

  return image;
};

export const Entry: FC<Props> = ({ className }) => {
  const { wpPage } = useStaticQuery<Queries.HomeEntryQuery>(staticQuery);
  const homeImage = useHomeImage({ wpPage });

  return (
    <Wrapper className={className ?? ''}>
      {homeImage && (
        <StyledImg
          {...homeImage}
          isDisableTransition={true}
          loading="eager"
          sizes={HOME_IMAGE_SIZES}
        />
      )}
    </Wrapper>
  );
};

const StyledImg = styled(Img)`
  border: 1px solid black;
  height: 100%;
  ${media.lessThanSiteBreakpoint} {
    position: fixed;
    z-index: 1000;
    left: ${({ theme }) => theme.structure.main.margin.horizontal.pc}px;
    bottom: 0;
    width: calc(
      100vw - ${({ theme }) => theme.structure.main.margin.horizontal.pc}px
    );
    height: 40vh;
  }
  ${media.lessThanIpadVertical} {
    left: ${({ theme }) => theme.structure.main.margin.horizontal.sp}px;
    width: calc(
      100vw - ${({ theme }) => theme.structure.main.margin.horizontal.sp}px
    );
  }
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  ${media.siteBreakpointOrMore} {
    height: 100%;
  }
`;

export default Entry;
